import React, { useMemo, useEffect, useRef, useState } from "react";
import ContentLayout from "../layouts/ContentLayout";
import { fetchLunarForIntDate } from "../utils/wkCommon";
import { isZh } from "../utils/language";

export default function GanzhiCalendarPage() {

  const currentYear = new Date().getFullYear();
  const activeRowRef = useRef(null);
  const [lunarToday, setLunarToday] = useState(null);
  const today = new Date();

  useEffect(() => {
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");

    const dateStr = `${y}-${m}-${d}`;

    fetchLunarForIntDate(dateStr)
      .then(setLunarToday)
      .catch(() => setLunarToday(null));
  }, []);

  useEffect(() => {
    if (activeRowRef.current) {
      activeRowRef.current.scrollIntoView({
        block: "center"
      });
    }
  }, []);

  // =========================
  // Heavenly Stems 天干
  // =========================
    const stems = [
    { zh: "甲", pinyin: "Jiǎ", element: "木 Kayu", yinYang: "陽 Yang" },
    { zh: "乙", pinyin: "Yǐ", element: "木 Kayu", yinYang: "陰 Yin" },
    { zh: "丙", pinyin: "Bǐng", element: "火 Api", yinYang: "陽 Yang" },
    { zh: "丁", pinyin: "Dīng", element: "火 Api", yinYang: "陰 Yin" },
    { zh: "戊", pinyin: "Wù", element: "土 Tanah", yinYang: "陽 Yang" },
    { zh: "己", pinyin: "Jǐ", element: "土 Tanah", yinYang: "陰 Yin" },
    { zh: "庚", pinyin: "Gēng", element: "金 Logam", yinYang: "陽 Yang" },
    { zh: "辛", pinyin: "Xīn", element: "金 Logam", yinYang: "陰 Yin" },
    { zh: "壬", pinyin: "Rén", element: "水 Air", yinYang: "陽 Yang" },
    { zh: "癸", pinyin: "Guǐ", element: "水 Air", yinYang: "陰 Yin" },
    ];

  // =========================
  // Earthly Branches 地支
  // =========================
  const branches = [
    { zh: "子", pinyin: "Zǐ", animal: "鼠 (Tikus)", time: "23:00–00:59" },
    { zh: "丑", pinyin: "Chǒu", animal: "牛 (Kerbau)", time: "01:00–02:59" },
    { zh: "寅", pinyin: "Yín", animal: "虎 (Macan)", time: "03:00–04:59" },
    { zh: "卯", pinyin: "Mǎo", animal: "兔 (Kelinci)", time: "05:00–06:59" },
    { zh: "辰", pinyin: "Chén", animal: "龍 (Naga)", time: "07:00–08:59" },
    { zh: "巳", pinyin: "Sì", animal: "蛇 (Ular)", time: "09:00–10:59" },
    { zh: "午", pinyin: "Wǔ", animal: "馬 (Kuda)", time: "11:00–12:59" },
    { zh: "未", pinyin: "Wèi", animal: "羊 (Kambing)", time: "13:00–14:59" },
    { zh: "申", pinyin: "Shēn", animal: "猴 (Monyet)", time: "15:00–16:59" },
    { zh: "酉", pinyin: "Yǒu", animal: "雞 (Ayam)", time: "17:00–18:59" },
    { zh: "戌", pinyin: "Xū", animal: "狗 (Anjing)", time: "19:00–20:59" },
    { zh: "亥", pinyin: "Hài", animal: "豬 (Babi)", time: "21:00–22:59" },
  ];

  // =========================
  // Ganzhi calculation
  // =========================
    function getGanzhi(year) {
        const baseYear = 1984;
        const diff = year - baseYear;
        const stemIndex = (diff % 10 + 10) % 10;
        const branchIndex = (diff % 12 + 12) % 12;

        const stem = stems[stemIndex];
        const branch = branches[branchIndex];

        return {
            gz: stem.zh + branch.zh,
            pinyin: stem.pinyin + " " + branch.pinyin,
            animal: branch.animal,
            element: stem.element,
            yinYang: stem.yinYang
        };
    }

    function preciseLichun(year) {
    const dateUTC = findSolarTerm(year, 315, 1); // around Feb
    const wib = new Date(dateUTC.getTime() + 7 * 60 * 60 * 1000);
    return formatDateTime(wib);
    }

    function preciseDongzhi(year) {
    const dateUTC = findSolarTerm(year, 270, 11); // around Dec
    const wib = new Date(dateUTC.getTime() + 7 * 60 * 60 * 1000);
    return formatDateTime(wib);
    }

    function formatDateTime(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");

    return `${y}-${m}-${d} ${hh}:${mm} WIB`;
    }

    // ===============================
    // ASTRONOMICAL SOLAR LONGITUDE
    // Accurate to ~1 minute
    // ===============================

    function toJulian(date) {
    return date / 86400000 + 2440587.5;
    }

    function fromJulian(jd) {
    return new Date((jd - 2440587.5) * 86400000);
    }

    function solarLongitude(jd) {
    const T = (jd - 2451545.0) / 36525;
    const L0 = 280.46646 + 36000.76983 * T;
    const M = 357.52911 + 35999.05029 * T;

    const C =
        (1.914602 - 0.004817 * T - 0.000014 * T * T) *
        Math.sin((M * Math.PI) / 180) +
        (0.019993 - 0.000101 * T) *
        Math.sin((2 * M * Math.PI) / 180) +
        0.000289 * Math.sin((3 * M * Math.PI) / 180);

    const trueLong = L0 + C;
    return (trueLong % 360 + 360) % 360;
    }

    function findSolarTerm(year, targetDegree, approxMonth) {
    // initial guess near expected month
    let date = new Date(Date.UTC(year, approxMonth, 1));
    let jd = toJulian(date);

    let step = 1; // 1 day
    for (let i = 0; i < 30; i++) {
        const lon = solarLongitude(jd);
        if (Math.abs(lon - targetDegree) < 0.5) break;
        jd += (targetDegree - lon) / 360 * 365.25;
    }

    // refine to minute precision
    let low = jd - 1;
    let high = jd + 1;

    for (let i = 0; i < 30; i++) {
        const mid = (low + high) / 2;
        const lon = solarLongitude(mid);
        if (lon < targetDegree) low = mid;
        else high = mid;
    }

    return fromJulian((low + high) / 2);
    }

    const yearData = useMemo(() => {
    const arr = [];
    for (let y = 1900; y <= 2099; y++) {
        const gz = getGanzhi(y);
        arr.push({
        year: y,
        ...gz,
        lichun: preciseLichun(y),
        dongzhi: preciseDongzhi(y)
        });
    }
    return arr;
    }, []);

  const mainContent = (
    <div style={{ width: "90%", padding: "1.5rem 2rem" }}>

      {/* ===== FULL WIDTH OVERRIDE ===== */}
      <style>
        {`
          .content-layout,
          .header-inner,
          .search-row,
          .tip-area,
          .content-body {
            max-width: 100% !important;
          }

        .content-layout .content-body {
            align:center;
            grid-template-columns: 1fr !important;
        }

          .content-list {
            padding: 0 !important;
          }

          .ganzhi-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 0.95rem;
          }

          .ganzhi-table thead {
            background: #f1f5fb;
            font-weight: 600;
          }

          .ganzhi-table th,
          .ganzhi-table td {
            padding: 0.6rem 0.8rem;
            border-bottom: 1px solid #e6eef8;
          }

          .ganzhi-table tbody tr:nth-child(even) {
            background: #fafcff;
          }

          .ganzhi-table tbody tr:hover {
            background: #eef4ff;
          }

          .ganzhi-table .first-col {
            font-weight: 600;
            text-align: center;
          }

          .ganzhi-table .center {
            text-align: center;
          }

          .ganzhi-table .active-row {
            background: #fff3cd !important;
            font-weight: 600;
          }

          .year-window {
            max-height: 20rem;
            overflow-y: auto;
            overflow-x: auto;
            border: 1px solid #e6eef8;
            border-radius: 0.5rem;
            margin-bottom: 2rem;
          }

            .year-window thead th {
            position: sticky;
            top: 0;
            background: #f1f5fb;
            z-index: 2;
            }

            /* Optional: tambahkan shadow tipis saat scroll */
            .year-window thead th {
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);
            }
            
 /* ============================= */
/* MOBILE LAYOUT – ONLY YEAR TABLE */
/* ============================= */

@media (max-width: 768px) {

  .year-table thead {
    display: none;
  }

  .year-table,
  .year-table tbody,
  .year-table tr,
  .year-table td {
    display: block;
    width: 100%;
  }

  .year-table tr {
    padding: 0.8rem;
    border-bottom: 1px solid #e6eef8;
  }

  .year-table td {
    padding: 0.25rem 0;
  }

  /* Gabungkan kolom menjadi 3 baris */

  .year-table td:nth-child(1)::before {
    content: "西曆 / 歲次: ";
    font-weight: 600;
  }

  .year-table td:nth-child(3)::before {
    content: "生肖 / 五行: ";
    font-weight: 600;
  }

  .year-table td:nth-child(5)::before {
    content: "節氣: ";
    font-weight: 600;
  }

/* ====================================== */
/* MOBILE YEAR TABLE – CLEAN FINAL       */
/* ====================================== */

@media (max-width: 768px) {

  .year-table thead {
    display: none;
  }

  .year-table,
  .year-table tbody,
  .year-table tr {
    display: block;
    width: 100%;
  }

  .year-table tr {
    padding: 1rem 0.8rem;
    border-bottom: 1px solid #e6eef8;
  }

  .year-table td {
    display: block;
    padding: 0.2rem 0;
    text-align: left;
    font-weight: 600;
  }

  /* ========================= */
  /* BARIS 1 – TAHUN (CENTER) */
  /* ========================= */

  .year-table td:nth-child(1) {
    text-align: center;
    font-weight: 600;
    font-size: 1rem;
    margin-bottom: 0.4rem;
  }

  .year-table td:nth-child(1)::before {
    content: "西曆 Tahun";
    display: block;
    font-size: 0.8rem;
    font-weight: 500;
    opacity: 0.7;
    margin-bottom: 0.2rem;
  }

  .year-table td:nth-child(2)::before {
    content: "歲次 Siklus Tahun (Suici): ";
    display: inline-block;
    font-weight: 400;
    margin-right: 0.5rem;
    }

  /* ========================= */
  /* BARIS 2 – SHIO & WUXING  */
  /* ========================= */

  .year-table td:nth-child(3)::before {
    content: "生肖 Shio: ";
    display: inline-block;
    font-weight: 400;
    margin-right: 0.5rem;
  }

  .year-table td:nth-child(4)::before {
    content: "五行 Unsur: ";
    display: inline-block;
    font-weight: 400;
    margin-right: 0.5rem;
  }


  /* ========================= */
  /* BARIS 3 – JIEQI          */
  /* ========================= */

  .year-table td:nth-child(5)::before {
    content: "立春 Lichun: ";
    display: inline-block;
    font-weight: 400;
    margin-right: 0.5rem;
  }

  .year-table td:nth-child(6)::before {
    content: "冬至 Dongzhi: ";
    display: inline-block;
    font-weight: 400;
    margin-right: 0.5rem;
  }

}
        `}
      </style>

      <div style={{
        margin: "0.2rem 0 0.5rem 0",
        padding: "0.8rem 1rem",
        background: "#f8fbff",
        border: "1px solid #e6eef8",
        borderRadius: "0.5rem"
      }}>
        <div style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "0.5rem"
        }}>
          {/* ICON */}
          <div style={{ fontSize: "1.25rem", lineHeight: "1.4" }}>
            📅
          </div>

          {/* TEXT BLOCK */}
          <div>
            {/* INTERNASIONAL */}
            <div style={{ fontSize: "1.25rem" }}>
              {isZh()
                ? `陽曆 ${today.getFullYear()}年${today.getMonth() + 1}月${today.getDate()}日`
                : today.toLocaleDateString("id-ID", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric"
                  })
              }
            </div>

            {/* LUNAR */}
            {lunarToday && (
              <div style={{ fontSize: "1rem", marginTop: "0.2rem", opacity: 0.85 }}>
                歲次{lunarToday.lunarGanZhi}
                {lunarToday.leapMonth ? " 閏" : " "}
                {lunarToday.lunarMonth}月 {lunarToday.lunarDay}日
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== YEAR TABLE ===== */}
      <h2 style={{ fontSize: "1.2rem", margin: "0.2rem 0 1rem 0" }}>
        節氣時間表 Daftar Tahun dan Perhitungan JieQi
      </h2>

      <div className="year-window">
        <table className="ganzhi-table year-table">
          <thead>
            <tr>
              <th>西曆 Tahun</th>
              <th>歲次 Suici</th>
              <th>生肖 Shio</th>
              <th>五行 Unsur</th>
              <th>立春 Li Chun</th>
              <th>冬至 Dong Zhi</th>
            </tr>
          </thead>
          <tbody>
            {yearData.map((y) => (
              <tr
                key={y.year}
                ref={y.year === currentYear ? activeRowRef : null}
                className={y.year === currentYear ? "active-row" : ""}
              >
                <td className="first-col">{y.year}</td>
                <td>{y.gz} ({y.pinyin})</td>
                <td>{y.animal}</td>
                <td>
                {y.element} {y.yinYang}
                </td>
                <td>{y.lichun}</td>
                <td>{y.dongzhi}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ===== STEMS ===== */}
      <h2 style={{ fontSize: "1.2rem", margin: "2rem 0 1rem 0" }}>
        天干 Tian Gan (Batang Langit / Heavenly Stems)
      </h2>

      <table className="ganzhi-table">
        <thead>
          <tr>
            <th>天干 Tian Gan</th>
            <th>拼音 Pinyin</th>
            <th>五行 Unsur</th>
          </tr>
        </thead>
        <tbody>
          {stems.map((s, i) => (
            <tr key={i}>
              <td className="first-col center" style={{ fontSize: "1.2rem" }}>
                {s.zh}
              </td>
              <td className="center">{s.pinyin}</td>
              <td className="center">{s.element} {s.yinYang}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ===== BRANCHES ===== */}
      <h2 style={{ fontSize: "1.2rem", margin: "2rem 0 1rem 0" }}>
        地支與十二時辰 Di Zhi (Cabang Bumi / Earthly Branches) dan 12 Masa Waktu
      </h2>

      <table className="ganzhi-table">
        <thead>
          <tr>
            <th>地支 Di Zhi</th>
            <th>拼音 Pinyin</th>
            <th>時辰 Waktu (WIB)</th>
            <th>生肖 Shio   </th>
          </tr>
        </thead>
        <tbody>
          {branches.map((b, i) => (
            <tr key={i}>
              <td className="first-col center" style={{ fontSize: "1.2rem" }}>
                {b.zh}
              </td>
              <td className="center">{b.pinyin}</td>
              <td className="center">{b.time}</td>
              <td>{b.animal}</td>
            </tr>
          ))}
        </tbody>
      </table>

    </div>
  );

  return (
    <ContentLayout
      title="干支曆與節氣時間表 Kalender Gan Zhi"
      hideSearch={true}
      singleColumn={true}
      mainNode={mainContent}
      hideHeaderActions={true}
    />
  );
}